import { inject, Injectable } from '@angular/core';

import { APP_CONFIG } from '@core/config';

type GtagEventParams = Record<string, unknown>;

type GtagUserData = {
  user_id: string;
  org_type: string;
  subscription_tier: string;
  sector: string;
  organisation_name?: string;
};

type GtagFunction = {
  (command: 'js', date: Date): void;
  (command: 'config', targetId: string, config?: GtagEventParams): void;
  (command: 'event', eventName: string, params?: GtagEventParams): void;
};

type GtagWindow = Window &
  typeof globalThis & {
    dataLayer: IArguments[];
    gtag?: GtagFunction;
  };

@Injectable({ providedIn: 'root' })
export class GtagService {
  gtag?: GtagFunction;
  userData: GtagUserData | undefined;

  private readonly config = inject(APP_CONFIG);

  constructor() {
    this.install();
  }

  install() {
    const gtagConfig = this.config.gtag;
    if (!gtagConfig?.enabled || !gtagConfig.key) {
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gtagConfig.key}`;

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }

    const gtagWindow = window as GtagWindow;
    gtagWindow.dataLayer = gtagWindow.dataLayer || [];

    function toDataLayerArguments(...values: unknown[]): IArguments {
      void values;
      // eslint-disable-next-line prefer-rest-params
      return arguments;
    }

    function gtag(command: 'js', date: Date): void;
    function gtag(command: 'config', targetId: string, config?: GtagEventParams): void;
    function gtag(command: 'event', eventName: string, params?: GtagEventParams): void;
    function gtag(command: string, ...rest: unknown[]): void {
      gtagWindow.dataLayer.push(toDataLayerArguments(command, ...rest));
    }

    gtag('js', new Date());
    gtag('config', gtagConfig.key, { send_page_view: false });

    this.gtag = gtag;
    gtagWindow.gtag = gtag;
  }

  event(action: string, params?: GtagEventParams) {
    if (this.gtag) {
      this.gtag('event', action, { event_category: this.config.gtag.category, ...params, ...this.userData });
      if (this.config.gtag.debug) {
        console.log('[gtag]', action, { ...params, ...this.userData });
      }
    }
  }

  setUser(params?: GtagUserData) {
    if (params) {
      return (this.userData = { ...params });
    }
    return (this.userData = undefined);
  }
}
